// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: live terminals in the Agents Terminals panel (konvo_api agents/AgentsTerminalSessions.ts).
// The person's own terminals for a chat on their machine: the ones they opened in T3 and the ones
// opened from Konvo, listed live, shown in a real terminal emulator and typed into. One connection per
// machine (utils/AgentTerminalLink.ts) while a panel uses it, kept a little after the last one closes
// so switching chats doesn't reconnect. Output goes straight to the emulator on screen (a sink per
// terminal); nothing is kept here beyond the list and each terminal's state.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {viewSocketUrl} from '@app/features/konvo_agents/state/AgentBrowser';
import {
	type LinkProblem,
	type LinkSocket,
	type LinkStatus,
	TerminalLink,
} from '@app/features/konvo_agents/utils/AgentTerminalLink';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import type {AgentMachineView} from '@konvo/schema/src/domains/konvo/Agents';
import {
	AGENT_TERMINAL_TOKEN_PROTOCOL_PREFIX,
	type AgentLiveTerminal,
	type AgentTerminalServerFrame,
	type AgentTerminalSession,
	type AgentTerminalSize,
	type AgentTerminalState,
} from '@konvo/schema/src/domains/konvo/AgentTerminals';
import {makeAutoObservable, observable, runInAction} from 'mobx';

/** A connection nobody uses any more closes after this long. */
const LINGER_MS = 30_000;

/** What the emulator on screen does with a terminal's output. */
export interface TerminalSink {
	/** Everything the terminal holds now (on attach, and after a reconnect): replaces the screen. */
	snapshot(data: string, size: AgentTerminalSize | null): void;
	output(data: string): void;
	/** The size the terminal has in T3 changed (terminals opened there). */
	size?(size: AgentTerminalSize): void;
}

export interface MachineLink {
	status: LinkStatus;
	problem: LinkProblem | null;
}

const threadKey = (machineId: string, threadId: string) => `${machineId}\u0000${threadId}`;
const streamKey = (machineId: string, threadId: string, id: string) => `${machineId}\u0000${threadId}\u0000${id}`;

/** Whether this person can use the machine's terminals live: its bridge has them and nothing turned them off. */
export const canUseTerminalSessions = (machine: AgentMachineView) =>
	machine.limits.send &&
	machine.limits.terminals !== false &&
	machine.limits.terminalsCap !== 'off' &&
	machine.tools.some((tool) => tool.can.terminalSessions === true);

const randomId = () =>
	typeof crypto !== 'undefined' && 'randomUUID' in crypto
		? crypto.randomUUID()
		: `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

/** Asks Konvo for a session and opens the relay's socket with it, as a page can (the token as a subprotocol). */
async function connectSocket(machineId: string): Promise<LinkSocket> {
	const response = await http.post<AgentTerminalSession>(Endpoints.KONVO_AGENTS_TERMINALS_SESSION(machineId), {
		body: {},
		mode: 'silent',
	});
	if (!response.ok)
		throw new HttpError({
			method: 'POST',
			path: '',
			status: response.status,
			body: response.body,
		});
	const session = response.body;
	return new WebSocket(viewSocketUrl(session.path), [
		session.protocol,
		`${AGENT_TERMINAL_TOKEN_PROTOCOL_PREFIX}${session.token}`,
	]) as unknown as LinkSocket;
}

export class AgentTerminalSessionsStore {
	/** Per machine: how its connection stands. */
	links = observable.map<string, MachineLink>();
	/** Per chat: its terminals, as the machine lists them. */
	lists = observable.map<string, Array<AgentLiveTerminal>>();
	/** Per terminal: running, exited, gone. */
	states = observable.map<string, AgentTerminalState>();
	/** Per terminal opened in T3: its size there. */
	sizes = observable.map<string, AgentTerminalSize>();
	/** The terminal picked in each chat's panel. */
	picked = observable.map<string, string>();
	/** A New terminal under way, per chat. */
	opening = observable.map<string, boolean>();
	/** The last thing a machine refused, for the panel to say. */
	notice = observable.map<string, LinkProblem>();
	private readonly cid = randomId();
	private readonly open = new Map<
		string,
		{link: TerminalLink; users: number; linger: ReturnType<typeof setTimeout> | null}
	>();
	private readonly sinks = new Map<string, TerminalSink>();
	/** Tests and the sandbox stand in for the socket. */
	connect: (machineId: string) => Promise<LinkSocket> = connectSocket;

	constructor() {
		makeAutoObservable<AgentTerminalSessionsStore, 'cid' | 'open' | 'sinks'>(this, {
			cid: false,
			open: false,
			sinks: false,
			connect: false,
		});
	}

	link(machineId: string): MachineLink | null {
		return this.links.get(machineId) ?? null;
	}

	list(machineId: string, threadId: string): Array<AgentLiveTerminal> | null {
		return this.lists.get(threadKey(machineId, threadId)) ?? null;
	}

	stateOf(machineId: string, threadId: string, id: string): AgentTerminalState | null {
		return this.states.get(streamKey(machineId, threadId, id)) ?? null;
	}

	sizeOf(machineId: string, threadId: string, id: string): AgentTerminalSize | null {
		return this.sizes.get(streamKey(machineId, threadId, id)) ?? null;
	}

	pickedId(machineId: string, threadId: string): string | null {
		return this.picked.get(threadKey(machineId, threadId)) ?? null;
	}

	pick(machineId: string, threadId: string, id: string) {
		this.picked.set(threadKey(machineId, threadId), id);
	}

	isOpening(machineId: string, threadId: string) {
		return this.opening.get(threadKey(machineId, threadId)) === true;
	}

	/** Uses the machine's connection (made on first use) and follows the chat; returns the release. */
	use(machineId: string, threadId: string): () => void {
		let entry = this.open.get(machineId);
		if (!entry) {
			const link = new TerminalLink({
				cid: this.cid,
				connect: () => this.connect(machineId),
				onFrame: (frame) => this.receive(machineId, frame),
				onStatus: (status, problem) => runInAction(() => this.links.set(machineId, {status, problem: problem ?? null})),
			});
			entry = {link, users: 0, linger: null};
			this.open.set(machineId, entry);
		}
		if (entry.linger) clearTimeout(entry.linger);
		entry.linger = null;
		entry.users++;
		entry.link.follow(threadId);
		const used = entry;
		let released = false;
		return () => {
			if (released) return;
			released = true;
			used.users--;
			if (used.users > 0) return;
			used.linger = setTimeout(() => this.drop(machineId), LINGER_MS);
		};
	}

	/** Shows a terminal: its output goes to `sink` until the returned function is called. */
	attach(machineId: string, threadId: string, id: string, sink: TerminalSink): () => void {
		const key = streamKey(machineId, threadId, id);
		this.sinks.set(key, sink);
		this.open.get(machineId)?.link.attach(threadId, id);
		return () => {
			if (this.sinks.get(key) !== sink) return;
			this.sinks.delete(key);
			this.open.get(machineId)?.link.detach(threadId, id);
		};
	}

	input(machineId: string, threadId: string, id: string, data: string) {
		this.open.get(machineId)?.link.input(threadId, id, data);
	}

	/** Only terminals opened from Konvo follow the panel's size; the machine refuses the others. */
	resize(machineId: string, threadId: string, id: string, cols: number, rows: number) {
		this.open.get(machineId)?.link.resize(threadId, id, cols, rows);
	}

	/** A new terminal for the chat in its folder, at the panel's size; picked once it is there. */
	async openTerminal(machineId: string, threadId: string, cols: number, rows: number): Promise<string | null> {
		const link = this.open.get(machineId)?.link;
		const key = threadKey(machineId, threadId);
		if (!link || this.opening.get(key)) return null;
		this.opening.set(key, true);
		this.notice.delete(machineId);
		try {
			const id = await link.openTerminal(threadId, cols, rows);
			runInAction(() => this.picked.set(key, id));
			return id;
		} catch (error) {
			runInAction(() => this.notice.set(machineId, error as LinkProblem));
			return null;
		} finally {
			runInAction(() => this.opening.delete(key));
		}
	}

	async closeTerminal(machineId: string, threadId: string, id: string): Promise<void> {
		const link = this.open.get(machineId)?.link;
		if (!link) return;
		try {
			await link.closeTerminal(threadId, id);
		} catch (error) {
			runInAction(() => this.notice.set(machineId, error as LinkProblem));
		}
	}

	retry(machineId: string) {
		this.open.get(machineId)?.link.retry();
	}

	clearNotice(machineId: string) {
		this.notice.delete(machineId);
	}

	private drop(machineId: string) {
		const entry = this.open.get(machineId);
		if (!entry || entry.users > 0) return;
		this.open.delete(machineId);
		entry.link.dispose();
		this.links.delete(machineId);
	}

	private receive(machineId: string, frame: AgentTerminalServerFrame) {
		switch (frame.t) {
			case 'list':
				this.lists.set(threadKey(machineId, frame.threadId), frame.terminals);
				for (const t of frame.terminals) {
					const key = streamKey(machineId, frame.threadId, t.id);
					const before = this.states.get(key);
					// The list says how each one stands; a live state (from its own stream) is newer.
					if (!before || before.status === 'starting' || ['running', 'idle'].includes(before.status))
						this.states.set(key, {status: t.status, ...(t.exitCode !== undefined ? {exitCode: t.exitCode} : {})});
				}
				return;
			case 'snap': {
				const key = streamKey(machineId, frame.threadId, frame.id);
				this.states.set(key, frame.state);
				if (frame.size) this.sizes.set(key, frame.size);
				this.sinks.get(key)?.snapshot(frame.data, frame.size ?? null);
				return;
			}
			case 'out':
				this.sinks.get(streamKey(machineId, frame.threadId, frame.id))?.output(frame.data);
				return;
			case 'state':
				this.states.set(streamKey(machineId, frame.threadId, frame.id), frame.state);
				return;
			case 'size': {
				const key = streamKey(machineId, frame.threadId, frame.id);
				this.sizes.set(key, frame.size);
				this.sinks.get(key)?.size?.(frame.size);
				return;
			}
			case 'error':
				// Answers to a request are the request's; the rest (a write the terminal refused) are said once.
				if (!frame.req) this.notice.set(machineId, {code: frame.code, message: frame.message});
				return;
		}
	}
}

export default new AgentTerminalSessionsStore();
